import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe2, HandHeart, ReceiptText, ShieldCheck } from "lucide-react";
import FundraisingSection from "../components/Donate/Fundraising/FundraisingSection";
import DonateBanner from "../components/Donate/DonateBanner";
import PageBanner from "../components/PageBanner";
import { useRegionalExperience } from "../hooks/useRegionalExperience";

const Donate = () => {
  const { t } = useTranslation();
  const { region, regionKey, switchRegion, siteConfig } = useRegionalExperience();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main id="donateTop" className="overflow-x-hidden bg-[#f7faf9] text-[#14201f]">
      <PageBanner
        {...(siteConfig.donatePage?.banner || {})}
        eyebrow={region.paymentLabel}
        backgroundImage={region.bannerImage}
        ctaButtons={[
          {
            label: `${t("donate.page.continueTo")} ${region.paymentLabel}`,
            href: region.donationPath,
            variant: "primary",
          },
          { label: t("donate.page.viewCampaigns"), href: "#campaigns", variant: "outline" },
        ]}
        rightSlot={
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-white/20 bg-[#241511]/55 p-5 shadow-lg shadow-black/30 backdrop-blur-md">
              <ReceiptText className="h-7 w-7 text-[#f5c45e]" />
              <h2 className="mt-5 text-xl font-semibold text-white">{t("donate.page.secureGivingPath")}</h2>
              <p className="mt-3 text-sm leading-7 text-white/85">
                {t("donate.page.secureGivingDesc", { currency: region.currency, provider: region.paymentProvider?.toUpperCase() })}
              </p>
            </div>
            <div className="rounded-lg border border-white/20 bg-[#241511]/55 p-5 shadow-lg shadow-black/30 backdrop-blur-md">
              <ShieldCheck className="h-7 w-7 text-[#f5c45e]" />
              <h2 className="mt-5 text-xl font-semibold text-white">{t("donate.page.transparentGiving")}</h2>
              <p className="mt-3 text-sm leading-7 text-white/85">
                {t("donate.page.transparentGivingDesc")}
              </p>
            </div>
            <div className="rounded-lg border border-white/20 bg-[#241511]/55 p-5 shadow-lg shadow-black/30 backdrop-blur-md sm:col-span-2">
              <Globe2 className="h-7 w-7 text-[#f5c45e]" />
              <h2 className="mt-5 text-xl font-semibold text-white">{t("donate.page.switchDonorRegion")}</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {["IN", "US"].map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => switchRegion(key)}
                    className={`rounded-md border px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] transition ${
                      regionKey === key
                        ? "border-[#f5c45e] bg-[#f5c45e] text-[#201711]"
                        : "border-white/35 text-white hover:bg-white/15"
                    }`}
                  >
                    {key === "IN" ? t("donate.page.india") : t("donate.page.usa")}
                  </button>
                ))}
              </div>
            </div>
          </div>
        }
      />

      <section className="border-b border-[#dfe8e6] bg-white py-10">
        <div className="container mx-auto grid gap-4 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
          {[
            [t("donate.page.pillars.healthcare.title"), t("donate.page.pillars.healthcare.text")],
            [t("donate.page.pillars.futureSkilling.title"), t("donate.page.pillars.futureSkilling.text")],
            [t("donate.page.pillars.reliefSeva.title"), t("donate.page.pillars.reliefSeva.text")],
          ].map(([title, text]) => (
            <div key={title} className="flex gap-4 rounded-lg border border-[#dfe8e6] p-5">
              <HandHeart className="mt-1 h-5 w-5 shrink-0 text-[#9d1f1c]" />
              <div>
                <h2 className="font-semibold text-[#14201f]">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-[#52615f]">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div id="campaigns">
        <FundraisingSection />
      </div>
      <DonateBanner />
    </main>
  );
};

export default Donate;
